<script lang="ts">
  /**
   * AppHost: loads an app by id and renders its root component.
   * Pseudocode:
   * - Given id prop, dynamic-import component via registry
   * - Show loading/progress and handle errors gracefully
   */
  import { onMount } from 'svelte';
  import { loadAppComponent, apps } from './registry';

  export let id: string;

  let C: any = null; // dynamically loaded component
  let error: string | null = null;
  let loaded = false;

  $: appEntry = apps.find(a => a.id === id);

  onMount(async () => {
    try {
      const mod = await loadAppComponent(id)();
      // Svelte component default export is the component constructor
      C = (mod as any).default;
      // Brief delay for smooth transition
      await new Promise(r => setTimeout(r, 50));
      loaded = true;
    } catch (e: any) {
      error = e?.message ?? 'Failed to load app';
    }
  });
</script>

<!-- Breadcrumb -->
<nav class="app-breadcrumb" aria-label="Breadcrumb">
  <a href="#/" class="breadcrumb-back" title="Back to All Tools">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
  </a>
  <div class="breadcrumb-divider" aria-hidden="true"></div>
  <a href="#/" class="breadcrumb-link">All Tools</a>
  <svg class="breadcrumb-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M9 18l6-6-6-6"/></svg>
  <span class="breadcrumb-current">{appEntry?.name ?? id}</span>
</nav>

{#if error}
  <div class="error-card">
    <div class="error-icon">
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round">
        <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
      </svg>
    </div>
    <div class="error-title">Failed to Load</div>
    <div class="error-message">{error}</div>
    <a href="#/" class="error-btn">← Back to Home</a>
  </div>
{:else if !C}
  <div class="skeleton-wrap">
    <div class="skeleton-header">
      <div class="skeleton-bar w-48 h-6"></div>
      <div class="skeleton-bar w-24 h-4 mt-2"></div>
    </div>
    <div class="skeleton-content">
      <div class="skeleton-bar w-full h-12"></div>
      <div class="skeleton-row">
        <div class="skeleton-bar flex-1 h-32"></div>
        <div class="skeleton-bar flex-1 h-32"></div>
      </div>
      <div class="skeleton-bar w-3/4 h-8"></div>
    </div>
  </div>
{:else}
  <div class="app-loaded" class:visible={loaded}>
    <svelte:component this={C} />
  </div>
{/if}

<style>
  /* Breadcrumb */
  .app-breadcrumb {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 1.25rem;
    animation: theme-fade-in 0.3s ease;
  }
  .breadcrumb-back {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border-radius: var(--theme-radius-sm);
    color: hsl(var(--theme-muted-foreground));
    text-decoration: none;
    transition: all 200ms ease;
    flex-shrink: 0;
  }
  .breadcrumb-back:hover {
    color: hsl(var(--theme-foreground));
    background: hsl(var(--theme-muted) / 0.15);
  }
  .breadcrumb-divider {
    width: 1px;
    height: 1rem;
    background: hsl(var(--theme-border) / 0.5);
    margin: 0 0.125rem;
  }
  .breadcrumb-link {
    font-size: 0.8125rem;
    font-weight: 500;
    color: hsl(var(--theme-muted-foreground));
    text-decoration: none;
    transition: color 200ms ease;
  }
  .breadcrumb-link:hover {
    color: hsl(var(--theme-primary));
  }
  .breadcrumb-chevron {
    color: hsl(var(--theme-muted-foreground) / 0.4);
    flex-shrink: 0;
  }
  .breadcrumb-current {
    font-size: 0.8125rem;
    font-weight: 600;
    color: hsl(var(--theme-foreground));
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* Error state */
  .error-card {
    max-width: 400px;
    margin: 4rem auto;
    padding: 2.5rem;
    border-radius: var(--theme-radius-xl);
    background: hsl(var(--theme-card));
    border: 1px solid hsl(var(--theme-destructive) / 0.2);
    text-align: center;
  }
  .error-icon {
    color: hsl(var(--theme-destructive));
    margin-bottom: 1rem;
  }
  .error-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: hsl(var(--theme-foreground));
    margin-bottom: 0.5rem;
  }
  .error-message {
    font-size: 0.8125rem;
    color: hsl(var(--theme-muted-foreground));
    margin-bottom: 1.5rem;
    line-height: 1.5;
  }
  .error-btn {
    display: inline-flex;
    padding: 0.5rem 1rem;
    border-radius: var(--theme-radius);
    background: hsl(var(--theme-muted) / 0.2);
    color: hsl(var(--theme-foreground));
    font-size: 0.8125rem;
    font-weight: 500;
    text-decoration: none;
    transition: all 200ms ease;
  }
  .error-btn:hover {
    background: hsl(var(--theme-muted) / 0.35);
  }

  /* Skeleton */
  .skeleton-wrap {
    max-width: 76rem;
    margin: 0 auto;
    padding: 2rem 1.5rem;
    animation: theme-fade-in 0.3s ease;
  }
  .skeleton-header { margin-bottom: 2rem; }
  .skeleton-content {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  .skeleton-row {
    display: flex;
    gap: 1rem;
  }
  .skeleton-bar {
    border-radius: var(--theme-radius);
    background: linear-gradient(90deg,
      hsl(var(--theme-muted) / 0.15) 0%,
      hsl(var(--theme-muted) / 0.25) 50%,
      hsl(var(--theme-muted) / 0.15) 100%
    );
    background-size: 200% 100%;
    animation: shimmer 1.5s ease-in-out infinite;
  }
  .w-full { width: 100%; }
  .w-3\/4 { width: 75%; }
  .w-48 { width: 12rem; }
  .w-24 { width: 6rem; }
  .h-4 { height: 1rem; }
  .h-6 { height: 1.5rem; }
  .h-8 { height: 2rem; }
  .h-12 { height: 3rem; }
  .h-32 { height: 8rem; }
  .mt-2 { margin-top: 0.5rem; }
  .flex-1 { flex: 1; }

  /* Loaded transition */
  .app-loaded {
    opacity: 0;
    transform: translateY(4px);
    transition: all 300ms cubic-bezier(0.16, 1, 0.3, 1);
  }
  .app-loaded.visible {
    opacity: 1;
    transform: translateY(0);
  }
</style>
